import { DatePipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { ViewDidEnter, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { filter, Observable, take, tap } from 'rxjs';
import { rangeCleanupZeroToHundred } from '../../../shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import { RadioBtnInventory } from '../../models/radio-btn-inventory.model';
import { FoodCostModalContainerComponent } from './components/food-cost-modal-container/food-cost-modal-container.component';
import { FoodCostViewModel } from './models/food-cost-view-model.model';
import { FoodCostCalculatorService } from './services/food-cost-calculator.service';
import { FoodCostReportState } from '@usf/ngrx-inventory';
import { CostReportCardEllipsisClick } from '@inventory/models/cost-report-model.model';
import { FoodCostRangeLoadingState } from './models/food-cost-constants';

@Component({
  selector: 'app-food-cost-calculator',
  templateUrl: './food-cost-calculator.page.html',
  styleUrls: ['./food-cost-calculator.page.scss'],
})
export class FoodCostCalculatorPage
  implements ViewWillEnter, ViewWillLeave, ViewDidEnter
{
  breadcrumbs: Crumb[];
  viewModel$: Observable<FoodCostViewModel>;
  foodPurchaseData$: Observable<any>;

  @ViewChild(FoodCostModalContainerComponent)
  foodCostModal: FoodCostModalContainerComponent;

  inventoriesForFoodCostModal$: Observable<RadioBtnInventory[]>;
  foodCostRangeLoadingState$: Observable<FoodCostRangeLoadingState>;

  startRangeIsBeingInteracted = false;
  endRangeIsBeingInteracted = false;
  rangeHasChanged = false;
  saveButtonHasBeenClicked = false;
  startRangeValue;
  endRangeValue;

  readonly foodCostRangeLoadingStateRef = FoodCostRangeLoadingState;

  constructor(
    readonly translateService: TranslateService,
    readonly datePipe: DatePipe,
    private panAppState: PanAppState,
    public loadingSpinnerService: LoadingSpinnerService,
    public foodCostCalculatorService: FoodCostCalculatorService,
    public platformService: PlatformService,
  ) {}

  ionViewWillLeave(): void {
    this.foodCostCalculatorService.stripExtraDepartmentsOnLeave();
    this.viewModel$ = undefined;
    this.foodCostCalculatorService.resetSort();
  }

  ionViewDidEnter(): void {
    this.foodCostCalculatorService.trackFoodCostCalculatorPageLoaded();
  }

  openFoodCostModal() {
    this.foodCostModal.openModal();
    this.foodCostCalculatorService.trackCreateNewReportModal();
  }

  handleModalSubmission = (submitMap: Map<string, any | any[]>) => {
    const foodCostRange = submitMap?.get('Food Cost Range')?.get(0);
    if (foodCostRange) {
      const one = Number(foodCostRange.one).valueOf();
      const two = Number(foodCostRange.two).valueOf();
      if (one < two) {
        this.foodCostCalculatorService.setRange(one, two);
      } else {
        this.foodCostCalculatorService.setRange(two, one);
      }
      this.foodCostCalculatorService
        .selectFoodCostRangeOnceLoaded$()
        .pipe(
          filter(value => {
            return !!value;
          }),
          take(1),
        )
        .subscribe((foodCostRange: any) => {
          this.startRangeValue = foodCostRange?.start;
          this.endRangeValue = foodCostRange?.end;
        });
    }
    this.foodCostCalculatorService.createFoodCostReport(submitMap);
  };

  ionViewWillEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.foodCostCalculator),
    );
    this.initializeBreadcrumbs();
    this.initializeFoodCostRangeValues();
    this.foodCostCalculatorService.setupInventoriesForFoodCost();
    this.viewModel$ = this.foodCostCalculatorService.getViewModel$();
    this.inventoriesForFoodCostModal$ =
      this.foodCostCalculatorService.selectInventoriesForFoodCostModal$();
    this.foodPurchaseData$ =
      this.foodCostCalculatorService.foodPurchaseData$.pipe(
        tap(data => {
          this.updateFoodPurchaseData(data);
        }),
      );
  }

  initializeBreadcrumbs() {
    var homeCrumbUrl = this.foodCostCalculatorService.platformService.isTouch
      .value
      ? '/touch/home'
      : '/desktop/home';

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: homeCrumbUrl,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.inventory',
        url: '/inventory',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.foodCostCalculator',
        url: '',
        active: true,
      },
    ];
  }

  initializeFoodCostRangeValues() {
    this.saveButtonHasBeenClicked = false;
    this.rangeHasChanged = false;
    this.foodCostRangeLoadingState$ =
      this.foodCostCalculatorService.selectFoodCostRangeLoadingState$();
    this.foodCostCalculatorService
      .selectFoodCostRangeOnceLoaded$()
      .pipe(take(1))
      .subscribe((foodCostRange: any) => {
        this.startRangeValue = foodCostRange?.start;
        this.endRangeValue = foodCostRange?.end;
      });
  }

  handleStartRangeChange(event: any, viewModel: FoodCostViewModel) {
    let value = rangeCleanupZeroToHundred(event?.detail?.value);
    this.startRangeValue = value;
    this.checkIfRangeHasChanged(viewModel);
  }

  handleEndRangeChange(event: any, viewModel: FoodCostViewModel) {
    let value = rangeCleanupZeroToHundred(event?.detail?.value);
    this.endRangeValue = value;
    this.checkIfRangeHasChanged(viewModel);
  }

  checkIfRangeHasChanged(viewModel: FoodCostViewModel) {
    this.rangeHasChanged =
      this.startRangeValue !== viewModel?.foodCostRange?.start ||
      this.endRangeValue !== viewModel?.foodCostRange?.end;
  }

  handleRangeSave() {
    const isInCorrectOrder = this.endRangeValue >= this.startRangeValue;
    this.foodCostCalculatorService.setRange(
      isInCorrectOrder ? this.startRangeValue : this.endRangeValue,
      isInCorrectOrder ? this.endRangeValue : this.startRangeValue,
    );
    this.rangeHasChanged = false;
    this.saveButtonHasBeenClicked = true;
    if (!isInCorrectOrder) {
      let tempVal = this.startRangeValue;
      this.startRangeValue = this.endRangeValue;
      this.endRangeValue = tempVal;
    }
  }

  openCostReportCardEllipsis(data: CostReportCardEllipsisClick) {
    this.foodCostCalculatorService.openCostReportCardEllipsis(data);
  }

  openCostReportDetailsCard(data: CostReportCardEllipsisClick) {
    this.foodCostCalculatorService.openFoodCostReportDetailsModal(data);
  }

  trackItem(index: number, item: FoodCostReportState) {
    return item ? item?.r4AddDtm : index;
  }

  handleSortClick(headerName: string) {
    this.foodCostCalculatorService.setSort(headerName);
  }

  requestFoodPurchaseDataUpdate = (data: any) => {
    this.foodCostCalculatorService.sendDataForFoodPurchase(data);
  };

  updateFoodPurchaseData = (data: any) => {
    this.foodCostModal?.updateFoodCostTotalFromOutsideContainer(data);
  };
}
